import React from 'react';
import { Link } from 'react-router-dom';
import notFoundSvg from '../assets/images/not-found.svg';
import accessForbiddenSvg from '../assets/images/access-forbidden.svg';
import veyloWordmarkBlack from '../assets/brand/veylo-wordmark-black.svg';
import veyloWordmarkWhite from '../assets/brand/veylo-wordmark-white.svg';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Configuration for each error status code
 */
interface ErrorConfig {
  statusCode: number;
  title: string;
  message: string;
  illustration: string;
  reasons: string[];
  customMessage?: string;
}

const ERROR_CONFIGS: Record<number, ErrorConfig> = {
  404: {
    statusCode: 404,
    title: 'So Sorry!',
    message: 'The page you are looking for cannot be found',
    illustration: notFoundSvg,
    reasons: [
      'The address may have been typed incorrectly.',
      'It may be a broken or outdated link.',
    ],
  },
  403: {
    statusCode: 403,
    title: 'So Sorry!',
    message: 'You don\'t have access to this page',
    illustration: accessForbiddenSvg,
    reasons: [
      'You may not have the required permissions.',
      'The resource may be restricted for you.',
    ],
  },
};

// Same brand button as the rest of the app: chartreuse fill with the on-brand
// (dark) text token, never white — white on chartreuse is unreadable.
const PRIMARY_ACTION_CLASS =
  'inline-block bg-bg-brand-solid text-text-primary-on-brand hover:bg-bg-brand-solid-hover ' +
  'font-bg-v2-sans font-bg-v2-semibold py-bg-v2-lg px-bg-v2-2xl rounded-bg-v2-md ' +
  'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-border-brand';

/**
 * Common Error Page Layout Component
 * This component provides the shared structure for all error pages
 */
interface ErrorPageLayoutProps {
  children: React.ReactNode;
}

export const ErrorPageLayout: React.FC<ErrorPageLayoutProps> = ({ children }) => {
  const { theme } = useTheme();

  return (
    <div className="min-h-screen bg-bg-secondary flex flex-col">
      {/* Top Logo Section */}
      <div className="flex w-full justify-center items-center p-8 gap-2">
        <img
          src={theme === 'dark' ? veyloWordmarkWhite : veyloWordmarkBlack}
          alt="Veylo"
          className="h-10 w-auto object-contain"
        />
      </div>

      {/* Main Content Section */}
      <div className="flex flex-1 items-center justify-center px-8 pb-16">
        {children}
      </div>
    </div>
  );
};

/**
 * Error Page Content Component
 * Renders the error-specific content within the common layout
 */
interface ErrorPageContentProps {
  config: ErrorConfig;
  redirectTo?: string;
  redirectLabel?: string;
}

const ErrorPageContent: React.FC<ErrorPageContentProps> = ({
  config,
  redirectTo = '/dashboard',
  redirectLabel = 'Go to Dashboard',
}) => {
  // Check for custom error message from session storage
  const errorMessage = sessionStorage.getItem('loginError');
  const displayMessage = errorMessage || config.message;
  
  // Clear the error message after reading
  if (errorMessage) {
    sessionStorage.removeItem('loginError');
  }

  return (
    <div className="max-w-6xl w-full flex gap-16 items-center">
      {/* Left Section - Text Content */}
      <div className="flex-1 flex flex-col">
        <h2 className="text-5xl font-bold text-text-primary mb-4">
          {config.title}
        </h2>
        <h4 className="text-2xl font-bold text-text-primary mb-6">
          {displayMessage}
        </h4>
        {config.reasons && config.reasons.length > 0 && (
          <div className="mb-8">
            <p className="text-lg font-semibold text-text-primary mb-3">
              Possible Reasons
            </p>
            <ul className="list-none text-lg text-text-secondary space-y-2">
              {config.reasons.map((reason, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2 text-text-tertiary">•</span>
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex gap-4">
          <Link
            to={redirectTo}
            className={PRIMARY_ACTION_CLASS}
          >
            {redirectLabel}
          </Link>
        </div>
      </div>

      {/* Right Section - Illustration */}
      <div className="flex-1 flex items-center justify-center">
        <img src={config.illustration} alt={config.title} className="w-full h-full" />
      </div>
    </div>
  );
};

/**
 * Main ErrorPage component that builds error pages based on status code
 */
interface ErrorPageProps {
  statusCode: number;
  redirectTo?: string;
  redirectLabel?: string;
  customMessage?: string;
}

const ErrorPage: React.FC<ErrorPageProps> = ({
  statusCode,
  redirectTo = '/dashboard',
  redirectLabel = 'Go to Dashboard',
  customMessage,
}) => {
  const config = ERROR_CONFIGS[statusCode];

  if (!config) {
    // Fallback for unknown status codes
    return (
      <ErrorPageLayout>
        <div className="max-w-6xl w-full text-center">
          <h2 className="text-5xl font-bold text-text-primary mb-4">
            Error {statusCode}
          </h2>
          <p className="text-xl text-text-secondary mb-8">
            An error occurred. Please try again later.
          </p>
          <Link
            to={redirectTo}
            className={PRIMARY_ACTION_CLASS}
          >
            {redirectLabel}
          </Link>
        </div>
      </ErrorPageLayout>
    );
  }

  // Override message if custom message is provided
  const finalConfig = customMessage
    ? { ...config, message: customMessage }
    : config;

  return (
    <ErrorPageLayout>
      <ErrorPageContent
        config={finalConfig}
        redirectTo={redirectTo}
        redirectLabel={redirectLabel}
      />
    </ErrorPageLayout>
  );
};

/**
 * 404 Not Found Error Page
 * Built by providing status code 404
 */
export const NotFoundErrorPage: React.FC = () => (
  <ErrorPage statusCode={404} />
);

/**
 * 403 Access Forbidden Error Page
 * Built by providing status code 403
 */
export const AccessForbiddenPage: React.FC = () => (
  <ErrorPage statusCode={403} />
);

export default ErrorPage;